import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import HelpButton from "../components/HelpButton";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, card, buttonPrimary, buttonSecondary, inputBase, pageHeading } from "../theme";
import { getAuthHeader, getLoginRedirect, getBusinessName } from "../session";
import { getWageToken, wageHeader } from "../wageUnlock";
import WageUnlockModal from "../components/WageUnlockModal";

const STATUS_LABEL = { outside: "Outside area", low_accuracy: "Low GPS accuracy", no_gps: "No GPS" };
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toInput = (s) => (s ? s.replace(" ", "T") : "");
const fromInput = (s) => (s ? s.replace("T", " ") : "");

function presetRange(key) {
  const now = new Date();
  const day = (now.getDay() + 6) % 7; // Monday = 0
  const start = new Date(now); const end = new Date(now);
  if (key === "this_week") start.setDate(now.getDate() - day);
  else if (key === "last_week") { start.setDate(now.getDate() - day - 7); end.setDate(start.getDate() + 6); }
  else if (key === "last_14") start.setDate(now.getDate() - 13);
  else if (key === "this_month") start.setDate(1);
  else if (key === "last_month") { start.setMonth(now.getMonth() - 1, 1); end.setDate(0); }
  return { from: ymd(start), to: ymd(end) };
}

export default function AttendancePage() {
  const [days, setDays] = useState(1);
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [settingsMsg, setSettingsMsg] = useState("");
  const [moduleOff, setModuleOff] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [dialog, setDialog] = useState(null); // {mode: 'add'|'edit'|'close', entry?}
  const [form, setForm] = useState({});
  const [formError, setFormError] = useState("");
  const [audit, setAudit] = useState([]);
  const [photo, setPhoto] = useState(null); // {entry, urls:{in,out}}
  const [range, setRange] = useState(presetRange("last_14"));
  const [layout, setLayout] = useState("detail");
  const [exportMsg, setExportMsg] = useState("");
  const [showWageUnlock, setShowWageUnlock] = useState(false);
  const authHeader = () => getAuthHeader();

  const load = (d = days) => {
    if (!getAuthHeader().Authorization) { window.location.href = getLoginRedirect(); return; }
    setBusinessName(getBusinessName());
    axios.get(endpoints.attendance(d), { headers: authHeader() })
      .then((res) => { setData(res.data); setModuleOff(false); })
      .catch((err) => {
        if (err.response?.data?.code === "module_disabled") setModuleOff(true);
        else if (err.response?.status === 401 || err.response?.status === 403) window.location.href = getLoginRedirect();
      });
  };
  const loadSettings = () => axios.get(endpoints.attendanceSettings, { headers: authHeader() })
    .then((res) => { setSettings(res.data); setSettingsDraft(res.data); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(days); loadSettings(); const t = setInterval(() => load(days), 60000); return () => clearInterval(t); }, [days]);

  const saveSettings = async () => {
    setSettingsMsg("");
    try {
      const res = await axios.patch(endpoints.attendanceSettings, {
        require_clock_in: settingsDraft.require_clock_in, gps_mode: settingsDraft.gps_mode, auto_close_hours: Number(settingsDraft.auto_close_hours),
      }, { headers: authHeader() });
      setSettings(res.data); setSettingsDraft(res.data); setSettingsMsg("Saved.");
    } catch (err) { setSettingsMsg(err.response?.data?.error || "Couldn't save."); }
  };

  const openDialog = (mode, entry) => {
    setFormError(""); setAudit([]);
    const now = new Date();
    const nowLocal = `${ymd(now)} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setForm(mode === "add"
      ? { employee_id: data.employees.find((e) => e.is_active)?.id || "", in_local: toInput(nowLocal), out_local: "", break_minutes: 0, reason: "" }
      : { in_local: toInput(entry.in_local), out_local: toInput(entry.out_local || nowLocal), break_minutes: entry.break_minutes, reason: "" });
    setDialog({ mode, entry });
    if (entry) axios.get(endpoints.attendanceAudit(entry.id), { headers: authHeader() }).then((res) => setAudit(res.data)).catch(() => {});
  };

  const submitDialog = async () => {
    setFormError("");
    const body = { reason: form.reason };
    if (dialog.mode === "add") Object.assign(body, { action: "add", employee_id: Number(form.employee_id), in_local: fromInput(form.in_local), out_local: fromInput(form.out_local), break_minutes: Number(form.break_minutes) || 0 });
    else if (dialog.mode === "edit") Object.assign(body, { action: "edit", entry_id: dialog.entry.id, in_local: fromInput(form.in_local), break_minutes: Number(form.break_minutes) || 0, ...(dialog.entry.open ? {} : { out_local: fromInput(form.out_local) }) });
    else Object.assign(body, { action: "close", entry_id: dialog.entry.id, out_local: fromInput(form.out_local) });
    try {
      await axios.post(endpoints.attendancePost, body, { headers: authHeader() });
      setDialog(null); load(days);
    } catch (err) { setFormError(err.response?.data?.error || "That didn't work."); }
  };

  const showPhotos = async (entry) => {
    const urls = {};
    for (const which of ["in", "out"]) {
      if (!entry[`has_${which}_photo`]) continue;
      try {
        const res = await axios.get(endpoints.attendancePhoto(entry.id, which), { headers: authHeader(), responseType: "blob" });
        urls[which] = URL.createObjectURL(res.data);
      } catch (e) { /* photo may have been purged */ }
    }
    setPhoto({ entry, urls });
  };
  const closePhotos = () => { if (photo) Object.values(photo.urls).forEach((u) => URL.revokeObjectURL(u)); setPhoto(null); };

  const download = async () => {
    setExportMsg("");
    try {
      const res = await axios.get(endpoints.attendanceExport(range.from, range.to, layout), { headers: { ...authHeader(), ...wageHeader() }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `hours_${range.from}_to_${range.to}${layout === "summary" ? "_summary" : ""}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      setExportMsg("Downloaded.");
    } catch (err) {
      let msg = "Couldn't export.";
      try { msg = JSON.parse(await err.response.data.text()).error || msg; } catch (e) { /* keep default */ }
      setExportMsg(msg);
    }
  };

  const grouped = {};
  (data?.entries || []).forEach((e) => { (grouped[e.date] = grouped[e.date] || []).push(e); });
  const dates = Object.keys(grouped).sort().reverse();
  const flags = (e) => [
    e.open && ["Open", "#b45309"], e.manual && ["Manual", "#6d28d9"], e.edited && ["Edited", "#6d28d9"], e.auto_closed && ["Auto-closed", "#b91c1c"],
    STATUS_LABEL[e.in_status] && [`In: ${STATUS_LABEL[e.in_status]}`, "#b91c1c"], STATUS_LABEL[e.out_status] && [`Out: ${STATUS_LABEL[e.out_status]}`, "#b91c1c"],
  ].filter(Boolean);

  if (moduleOff) {
    return (
      <div style={{ display: "flex", background: colors.bgPage, minHeight: "100vh" }}>
        <Sidebar businessName={businessName} />
        <div className="app-content" style={{ flex: 1, padding: space[6], maxWidth: 720 }}>
          <h1 style={pageHeading}>Attendance</h1>
          <div style={{ ...card, marginTop: space[4] }}>
            <p style={{ fontFamily: fonts.body, color: colors.textSecondary, marginTop: 0 }}>The Employee module isn't turned on for your restaurant yet.</p>
            <Link to="/settings" style={{ ...buttonPrimary, textDecoration: "none", display: "inline-block" }}>Request it under Settings</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", background: colors.bgPage, minHeight: "100vh" }}>
      <Sidebar businessName={businessName} />
      <div className="app-content" style={{ flex: 1, padding: space[6], maxWidth: 1100 }}>
        <h1 style={pageHeading}>Attendance <HelpButton page="attendance" /></h1>

        {settings && settingsDraft && (
          <div style={{ ...card, marginTop: space[3] }} data-testid="attendance-settings">
            <div style={styles.cardTitle}>Rules</div>
            <div style={styles.settingsRow}>
              <label style={styles.checkRow}>
                <input type="checkbox" checked={!!settingsDraft.require_clock_in} onChange={(e) => setSettingsDraft({ ...settingsDraft, require_clock_in: e.target.checked })} />
                Require clock-in: linked staff accounts are view-only while clocked out
              </label>
              <label style={styles.field}>GPS check
                <select style={inputBase} value={settingsDraft.gps_mode} onChange={(e) => setSettingsDraft({ ...settingsDraft, gps_mode: e.target.value })}>
                  <option value="enforce">Enforce: must be at the restaurant</option>
                  <option value="warn">Warn: allow but flag</option>
                  <option value="off">Off</option>
                </select>
              </label>
              <label style={styles.field}>Auto-close after (hours)
                <input style={{ ...inputBase, width: 90 }} type="number" min="4" max="24" value={settingsDraft.auto_close_hours} onChange={(e) => setSettingsDraft({ ...settingsDraft, auto_close_hours: e.target.value })} />
              </label>
              <button style={buttonPrimary} onClick={saveSettings}>Save rules</button>
              {settingsMsg && <span style={{ fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary }}>{settingsMsg}</span>}
            </div>
            {settings.gps_mode !== "off" && !settings.location_set && (
              <div style={styles.warn}>The restaurant location isn't set, so nobody can clock in while GPS is on. <Link to="/settings">Set it under Settings → Restaurant Location.</Link></div>
            )}
            <p style={styles.hint}>Restaurant time zone: {settings.timezone}. GPS radius: {settings.radius_m} m. Photos stay visible for {settings.retention_photo_days} days and hours for {settings.retention_hours_days} days (set by Alvna); older records are moved to an archive, not lost. Ask Alvna if you need them back.</p>
          </div>
        )}

        {data && (
          <>
            <div style={{ ...card, marginTop: space[3] }} data-testid="whos-in">
              <div style={styles.cardTitle}>Who's in now</div>
              {data.employees.filter((e) => e.clocked_in).length === 0 ? (
                <p style={styles.hint}>Nobody is clocked in.</p>
              ) : (
                <div style={{ display: "flex", gap: space[2], flexWrap: "wrap" }}>
                  {data.employees.filter((e) => e.clocked_in).map((e) => (
                    <span key={e.id} style={styles.inChip}>{e.name} <span style={{ opacity: 0.7 }}>since {e.since_local.slice(11)}</span></span>
                  ))}
                </div>
              )}
            </div>

            <div style={{ ...card, marginTop: space[3] }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: space[2], flexWrap: "wrap" }}>
                <div style={styles.cardTitle}>History</div>
                <div style={{ display: "flex", gap: space[2], alignItems: "center", flexWrap: "wrap" }}>
                  <label style={{ fontFamily: fonts.body, fontSize: 13 }}>Show last{" "}
                    <select style={{ ...inputBase, width: "auto", display: "inline-block" }} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Days to show">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n === 1 ? "1 day (today)" : `${n} days`}</option>)}
                    </select>
                  </label>
                  <button style={buttonSecondary} onClick={() => openDialog("add")}>Add entry</button>
                </div>
              </div>
              {dates.length === 0 && <p style={styles.hint}>No hours recorded in this period.</p>}
              {dates.map((date) => (
                <div key={date} style={{ marginTop: space[3] }}>
                  <div style={styles.dateHead}>{date}</div>
                  {grouped[date].map((e) => (
                    <div key={e.id} style={styles.entry} data-testid="attendance-entry">
                      <div style={{ flex: "1 1 160px", fontWeight: 600 }}>{e.employee_name}</div>
                      <div style={{ flex: "1 1 170px" }}>{e.in_local.slice(11)} to {e.out_local ? e.out_local.slice(11) : "…"}{e.out_local && e.out_local.slice(0, 10) !== e.in_local.slice(0, 10) ? " (+1)" : ""}</div>
                      <div style={{ flex: "0 0 110px" }}>{e.open ? "open" : e.hhmm}{e.break_minutes ? ` (−${e.break_minutes}m)` : ""}</div>
                      <div style={{ flex: "2 1 200px", display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {flags(e).map(([t, c]) => <span key={t} style={{ ...styles.flag, color: c, borderColor: c }}>{t}</span>)}
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {(e.has_in_photo || e.has_out_photo) && <button style={styles.smallBtn} onClick={() => showPhotos(e)}>Photos</button>}
                        {e.open ? <button style={styles.smallBtn} onClick={() => openDialog("close", e)}>Close shift</button> : null}
                        <button style={styles.smallBtn} onClick={() => openDialog("edit", e)}>Edit</button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              {data.employees.length > 0 && dates.length > 0 && (
                <div style={styles.totals}>
                  {data.employees.filter((e) => e.total_minutes > 0).map((e) => <span key={e.id}>{e.name}: <strong>{e.total_hhmm}</strong></span>)}
                </div>
              )}
            </div>

            <div style={{ ...card, marginTop: space[3] }} data-testid="export-panel">
              <div style={styles.cardTitle}>Payroll export (CSV)</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: space[2] }}>
                {[["this_week", "This week"], ["last_week", "Last week"], ["last_14", "Last 14 days"], ["this_month", "This month"], ["last_month", "Last month"]].map(([k, label]) => (
                  <button key={k} style={styles.smallBtn} onClick={() => setRange(presetRange(k))}>{label}</button>
                ))}
              </div>
              <div style={{ display: "flex", gap: space[2], alignItems: "flex-end", flexWrap: "wrap" }}>
                <label style={styles.field}>From <input style={inputBase} type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></label>
                <label style={styles.field}>To <input style={inputBase} type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></label>
                <label style={styles.field}>Layout
                  <select style={inputBase} value={layout} onChange={(e) => setLayout(e.target.value)}>
                    <option value="detail">Detail: one row per shift</option>
                    <option value="summary">Summary: one row per employee</option>
                  </select>
                </label>
                <button style={buttonPrimary} onClick={download}>Download CSV</button>
                {getWageToken()
                  ? <span style={{ fontFamily: fonts.body, fontSize: 12, color: "#166534" }}>🔓 Includes pay amounts</span>
                  : <button type="button" style={{ background: "none", border: "none", color: colors.brandBlue, fontSize: 12, fontFamily: fonts.body, cursor: "pointer", padding: 0, fontWeight: 600 }} onClick={() => setShowWageUnlock(true)}>🔒 Include pay amounts</button>}
                {exportMsg && <span style={{ fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary }}>{exportMsg}</span>}
              </div>
              <p style={styles.hint}>Times are in your restaurant's time zone; up to about 3 months at a time. Opens in Excel. Open shifts are marked and not counted.</p>
            </div>
          </>
        )}

        {dialog && (
          <div style={styles.overlay} onClick={() => setDialog(null)}>
            <div style={styles.modal} role="dialog" aria-label={dialog.mode === "add" ? "Add entry" : dialog.mode === "close" ? "Close shift" : "Edit entry"} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ fontFamily: fonts.display, fontSize: 18, margin: `0 0 ${space[3]}` }}>
                {dialog.mode === "add" ? "Add an entry" : dialog.mode === "close" ? `Close shift: ${dialog.entry.employee_name}` : `Edit: ${dialog.entry.employee_name}`}
              </h2>
              {dialog.mode === "add" && (
                <label style={styles.field}>Employee
                  <select style={inputBase} value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
                    {data.employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </label>
              )}
              {dialog.mode !== "close" && <label style={styles.field}>Clock in <input style={inputBase} type="datetime-local" value={form.in_local} onChange={(e) => setForm({ ...form, in_local: e.target.value })} /></label>}
              {!(dialog.mode === "edit" && dialog.entry.open) && (
                <label style={styles.field}>Clock out {dialog.mode === "add" && "(optional)"} <input style={inputBase} type="datetime-local" value={form.out_local} onChange={(e) => setForm({ ...form, out_local: e.target.value })} /></label>
              )}
              {dialog.mode !== "close" && <label style={styles.field}>Break (minutes) <input style={inputBase} type="number" min="0" value={form.break_minutes} onChange={(e) => setForm({ ...form, break_minutes: e.target.value })} /></label>}
              <label style={styles.field}>Reason (required) <input style={inputBase} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Forgot to clock out" maxLength={255} /></label>
              {formError && <div style={{ color: "#b91c1c", fontFamily: fonts.body, fontSize: 13 }} role="alert">{formError}</div>}
              <div style={{ display: "flex", gap: space[2], marginTop: space[3] }}>
                <button style={buttonPrimary} onClick={submitDialog}>Save</button>
                <button style={buttonSecondary} onClick={() => setDialog(null)}>Cancel</button>
              </div>
              {audit.length > 0 && (
                <div style={{ marginTop: space[3], fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>Change history</div>
                  {audit.map((a, i) => <div key={i}>{a.when_local}, {a.actor}: {a.action} ("{a.reason}")</div>)}
                </div>
              )}
            </div>
          </div>
        )}

        {photo && (
          <div style={styles.overlay} onClick={closePhotos}>
            <div style={styles.modal} role="dialog" aria-label="Photos" onClick={(e) => e.stopPropagation()}>
              <h2 style={{ fontFamily: fonts.display, fontSize: 18, margin: `0 0 ${space[3]}` }}>{photo.entry.employee_name}: {photo.entry.date}</h2>
              <div style={{ display: "flex", gap: space[3], flexWrap: "wrap" }}>
                {["in", "out"].map((w) => (
                  <div key={w} style={{ flex: "1 1 200px" }}>
                    <div style={{ fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>Clock {w} {w === "in" ? photo.entry.in_local.slice(11) : (photo.entry.out_local || "").slice(11)}</div>
                    {photo.urls[w] ? <img src={photo.urls[w]} alt={`Clock ${w} snapshot`} style={{ width: "100%", borderRadius: radius.sm }} /> : <div style={styles.noPhoto}>{photo.entry[`has_${w}_photo`] ? "Photo unavailable" : "No photo"}</div>}
                  </div>
                ))}
              </div>
              <p style={styles.hint}>Photos stay visible here for {settings ? settings.retention_photo_days : 30} days; older ones are archived.</p>
              <button style={buttonSecondary} onClick={closePhotos}>Close</button>
            </div>
          </div>
        )}
        {showWageUnlock && (
          <WageUnlockModal onUnlock={() => setShowWageUnlock(false)} onClose={() => setShowWageUnlock(false)} />
        )}
      </div>
    </div>
  );
}

const styles = {
  cardTitle: { fontFamily: fonts.display, fontWeight: 600, fontSize: 15, marginBottom: 6, color: colors.textPrimary },
  hint: { fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, margin: `${space[2]} 0 0` },
  warn: { fontFamily: fonts.body, fontSize: 13, background: "#fef3c7", color: "#92400e", padding: space[2], borderRadius: radius.sm, marginTop: space[2] },
  settingsRow: { display: "flex", gap: space[4], flexWrap: "wrap", alignItems: "flex-end" },
  checkRow: { display: "flex", alignItems: "center", gap: 8, fontFamily: fonts.body, fontSize: 13, color: colors.textPrimary },
  field: { display: "flex", flexDirection: "column", gap: 4, maxWidth: "100%", minWidth: 0, fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary, marginBottom: space[2] },
  inChip: { background: "#dcfce7", color: "#166534", borderRadius: 999, padding: "4px 12px", fontFamily: fonts.body, fontSize: 13, fontWeight: 600 },
  dateHead: { fontFamily: fonts.display, fontSize: 13, fontWeight: 600, color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`, paddingBottom: 4 },
  entry: { display: "flex", gap: space[3], alignItems: "center", flexWrap: "wrap", padding: `${space[2]} 0`, borderBottom: `1px solid ${colors.border}`, fontFamily: fonts.body, fontSize: 14, color: colors.textPrimary },
  flag: { border: "1px solid", borderRadius: 999, padding: "1px 8px", fontSize: 11, fontWeight: 600 },
  smallBtn: { padding: "6px 10px", fontSize: 12, borderRadius: radius.sm, border: `1px solid ${colors.border}`, background: colors.surfaceMuted, cursor: "pointer" },
  totals: { display: "flex", gap: space[4], flexWrap: "wrap", marginTop: space[3], fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: space[3] },
  modal: { background: colors.surface, borderRadius: radius.md, padding: space[5], width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", boxSizing: "border-box" },
  noPhoto: { background: colors.surfaceMuted, color: colors.textMuted, borderRadius: radius.sm, padding: space[4], textAlign: "center", fontFamily: fonts.body, fontSize: 12 },
};
